import { DELHI_CORDINATES } from "@Services/constants";
import { validImageTypes } from "../../cubeComponent/constants/globalVariable";
import get from "lodash/get";
import _ from "lodash";
import type { PortalUnits } from "@Actions/Viewer";
import { isNonZero } from "@Helpers/numberCondition";

export {
  getProjectWorkspacesListPageUrl,
  getProjectRiskManagementListPageUrl,
  getProjectIssuesPageUrl,
  getProjectFilesPageUrl,
  getProjectTransmittalsPageUrl,
  getSingleTransmittalPageUrl,
  getProjectTasksPageUrl,
  getProjectEstimatesPageUrl,
  getEstimatePageUrl,
  getProjectSchedulesPageUrl,
  getProjectFormsPageUrl,
  getProjectDashboardPageUrl,
  getPortalProjectsUrl,
  getUserProjects,
  getProjectMailboxPageUrl,
  getProjectReviewsPageUrl,
  getProjectContractPageUrl,
  getReviewUrl,
  getProjectGeneralClashPageUrl,
  getProjectTemplatesUrl,
  getClashDetailsPageUrl,
  getEstimateUrl,
  getScheduleUrl,
  getContractListUrl,
  getProjectSettingsPageUrl,
  getContractDetailUrl,
  getFormsListUrl,
  getProjectPageUrl,
  getProjectAssetsPageUrl,
} from "@Features/projects/utils/projectUrls";

export const getCustomModulePageUrl = (moduleId: string, selectedTab: string) =>
  `/portalSettings/formsAndFields?${new URLSearchParams({ selectedCategory: moduleId, selectedTab }).toString()}`;

export const getAllNotificationPageUrl = ({
  section,
  module,
  ticketId,
  supportType,
  referrerUrl,
}: {
  section?: string;
  module?: string;
  ticketId?: string;
  supportType?: string;
  referrerUrl?: string;
}) => {
  const searchParams = new URLSearchParams("");

  const url = `/notifications`;
  if (section) {
    searchParams.set("section", section);
  }
  if (supportType) {
    searchParams.set("supportType", supportType);
  }
  if (ticketId) {
    searchParams.set("ticketId", ticketId);
  }
  if (module) {
    searchParams.set("module", module);
  }
  if (referrerUrl) {
    searchParams.set("referrerUrl", referrerUrl);
  }

  return `${url}?${searchParams.toString()}`;
};

export const getPortalFilesUrl = () => `/Files?byPortal=true`;

export const getTemplateURL = (props: { selectedCategory?: string; selectedTab?: string }) => {
  const { selectedCategory, selectedTab } = props;
  const url = `/portalSettings/formsAndFields`;
  const searchParams = new URLSearchParams();

  if (selectedCategory) {
    searchParams.set("selectedCategory", selectedCategory.toLowerCase());
  }
  if (selectedTab) {
    searchParams.set("selectedTab", selectedTab);
  }
  return `${url}?${searchParams.toString()}`;
};

export const getPortalOrganizationUrl = "/portalSettings/organisations";

export const getNomenclatureTemplatesUrl = () => `/portalSettings/nomenclature`;

export const chr = (codePt: number) => {
  if (codePt > 0xffff) {
    codePt -= 0x10000;
    return String.fromCharCode(0xd800 + (codePt >> 10), 0xdc00 + (codePt & 0x3ff));
  }
  return String.fromCharCode(codePt);
};

// Callers pass either an alpha symbol or an already-numeric value, hence the union rather than
// `string`; it is normalised to its uppercased string form before the lookup.
export const charToNum = (alpha: string | number) => {
  const symbol = String(alpha).toUpperCase();
  for (let i = 0; ; i++) {
    if (symbol == numToChar(i)) {
      return i;
    }
  }
};

export const numToChar = (number: number): string => {
  const numeric = (number - 1) % 26;
  const letter = String.fromCodePoint(65 + numeric);
  const number2 = Math.floor((number - 1) / 26);
  if (number2 > 0) {
    return numToChar(number2) + letter;
  }
  return letter;
};

export const toRoman = (number: number): string => {
  let num = Math.floor(number);
  let val;
  let s = "";
  let i = 0;
  const v = [1000, 900, 500, 400, 100, 90, 50, 40, 10, 9, 5, 4, 1];
  const r = ["M", "CM", "D", "CD", "C", "XC", "L", "XL", "X", "IX", "V", "IV", "I"];

  function toBigRoman(n: number): string {
    let ret = "";
    let rem = n;
    while (rem > 1000) {
      let prefix = "";
      let suffix = "";
      let n = rem;
      const s = `${rem}`;
      let magnitude = 1;
      while (n > 1000) {
        n /= 1000;
        magnitude *= 1000;
        prefix += "(";
        suffix += ")";
      }
      const n1 = Math.floor(n);
      rem = Number(s) - n1 * magnitude;
      ret += prefix + toRoman(n1) + suffix;
    }
    return ret + toRoman(rem);
  }

  if (number - num || num < 1) num = 0;
  if (num > 3999) return toBigRoman(num);

  while (num) {
    val = v[i] ?? 0;
    if (!val) {
      break;
    }
    while (num >= val) {
      num -= val;
      s += r[i] ?? "";
    }
    ++i;
  }
  return s;
};

export const fromRoman = (roman: string, accept?: boolean): number => {
  const s = roman.toUpperCase().replace(/ +/g, "");
  const L = s.length;
  let sum = 0;
  let i = 0;
  let next;
  let val;
  const R: { [key: string]: number } = {
    M: 1000,
    D: 500,
    C: 100,
    L: 50,
    X: 10,
    V: 5,
    I: 1,
  };

  function fromBigRoman(rn: string): number {
    let n = 0;
    let x;
    let n1;
    let S;
    const rx = /(\(*)([MDCLXVI]+)/g;

    while ((S = rx.exec(rn)) != null) {
      const group = S[1] ?? "";
      const numeral = S[2] ?? "";
      x = group.length;
      n1 = fromRoman(numeral);
      if (isNaN(n1)) return NaN;
      if (x) n1 *= Math.pow(1000, x);
      n += n1;
    }
    return n;
  }

  if (/^[MDCLXVI)(]+$/.test(s)) {
    if (s.indexOf("(") == 0) return fromBigRoman(s);

    while (i < L) {
      val = R[s.charAt(i++)] ?? 0;
      next = R[s.charAt(i)] || 0;
      if (next - val > 0) val *= -1;
      sum += val;
    }
    if (accept || toRoman(sum) === s) return sum;
  }
  return NaN;
};

/** Numbering-system code stored on a BOQ/WBS column: 0 = plain, 1/2 = alpha, 3/4 = roman. */
type NumberSystem = number;

function getNumberFromNumberSystem(value: string | number, fromNumberSystem: NumberSystem): number {
  if ([1, 2].includes(fromNumberSystem)) {
    return charToNum(value);
  }

  if ([3, 4].includes(fromNumberSystem)) {
    // `fromRoman` uppercases its argument, so it needs the string form of a numeric symbol.
    return fromRoman(String(value));
  }

  // 0 = plain, and any code outside the documented 0-4 range: the value is already the number.
  return Number(value);
}

export function getSymbolForNumber(
  number: string | number,
  toNumberSystem: NumberSystem,
  fromNumberSystem?: NumberSystem,
) {
  // The source value may be an alpha/roman symbol; `numToChar`/`toRoman` below both need the
  // numeric ordinal, which is the coercion the converters used to perform implicitly.
  let convertedNumber = Number(number);

  if (isNonZero(fromNumberSystem)) {
    convertedNumber = getNumberFromNumberSystem(number, fromNumberSystem);
  }

  if (toNumberSystem == 1) {
    return numToChar(convertedNumber).toLowerCase();
  }

  if (toNumberSystem == 2) {
    return numToChar(convertedNumber).toUpperCase();
  }

  if (toNumberSystem == 3) {
    return toRoman(convertedNumber).toLowerCase();
  }

  if (toNumberSystem == 4) {
    return toRoman(convertedNumber).toUpperCase();
  }

  return number;
}

// The index arrives as a number from the file rows and as a string from `Estimation/Details` (its own
// `SelectedFile.indexAtProjectLevel ?? ""`); it is only interpolated, so both are taken as they are.
export const prepareEDN = (
  projectCustomId: string | number | undefined,
  indexAtProjectLevel: string | number | undefined,
) => `${projectCustomId}-${indexAtProjectLevel}`;

/** The avatar-form state this validator writes its result back into. */
interface AvatarFormState {
  logoObj?: File;
  error: { logo: string };
}

export const validateAvatar = (e: { target?: { files?: FileList | null } }, stateData: AvatarFormState) => {
  const file = e.target?.files;
  const data = stateData;
  const selected = file?.[0];
  if (selected && validImageTypes.includes(selected.type)) {
    data.logoObj = selected;
    data.error.logo = "";
  } else {
    data.error.logo = "Please select jpg, jpeg, png file only.";
  }
  return data;
};

export const validateEmail = (emails: string | string[]) => {
  if (typeof emails == "string") {
    emails = [emails];
  }
  for (const email of emails) {
    const valid = String(email)
      .toLowerCase()
      .match(/^(?!\.)(?!.*\.\.)([a-z0-9_'+.-]*)[a-z0-9_+-]@([a-z0-9][a-z0-9-]*\.)+[a-z]{2,}$/i);
    if (!valid) {
      return false;
    }
  }
  return true;
};

export const getTargetValue = (e: { value: unknown }) => e.value;

export const getSelectedIndex = (list: { value?: unknown }[] | undefined, compareTo: unknown) => {
  let indexReturn = -1;
  list?.map((s, index: number) => {
    if (s.value === compareTo) {
      indexReturn = index;
    }
  });
  return indexReturn;
};

export function getLocationCoordinates() {
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const locationCoordinates = [position.coords.latitude, position.coords.longitude];
        resolve(locationCoordinates);
      },
      () => {
        resolve([DELHI_CORDINATES.lat, DELHI_CORDINATES.lng]);
      },
    );
  });
}

let recaptchaLoadPromise: Promise<void> | null = null;

/**
 * `ready` queues a callback until the reCAPTCHA API is usable. It is missing from the ambient
 * `Window["grecaptcha"]` shape — react-app-env.d.ts declares only the `execute` the signup screens
 * call — so the member is checked here instead of read off a shape that does not declare it. The
 * source fix is one line in that ambient declaration: `ready(callback: () => void): void;`.
 */
const hasRecaptchaReady = (value: unknown): value is { ready: (callback: () => void) => void } =>
  typeof value === "object" && value !== null && "ready" in value && typeof value.ready === "function";

export function loadRecaptchaScripts(onReady?: () => void) {
  const siteKey = process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY;
  if (!siteKey) {
    onReady?.();
    return;
  }
  document.body.classList.remove("recaptcha-hidden");
  recaptchaLoadPromise ??= new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?${new URLSearchParams({ render: siteKey }).toString()}`;
    script.async = false;
    script.onload = () => {
      const style = document.createElement("style");
      style.innerHTML = `.grecaptcha-badge { left: 0.25rem !important; right: auto !important; width: 4.375rem !important; overflow: hidden !important; transition: all 0.3s ease !important; } .grecaptcha-badge:hover { width: 16rem !important; } body.recaptcha-hidden .grecaptcha-badge { visibility: hidden !important; }`;
      document.head.appendChild(style);
      const recaptcha: unknown = window.grecaptcha;
      if (hasRecaptchaReady(recaptcha)) {
        // `ready` invokes its callback with no arguments, so the wrapper is what `resolve` received before.
        recaptcha.ready(() => resolve());
      }
    };
    document.body.appendChild(script);
  });
  if (onReady) {
    // Fire-and-forget: loadRecaptcha has a void contract; onReady runs when the script lands.
    void recaptchaLoadPromise.then(onReady);
  }
}

const CHAT_BOT_HIDDEN_STYLE_ID = "chatbot-hidden-style";

export function hideRecaptchaBadge() {
  if (!process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY) return;
  document.body.classList.add("recaptcha-hidden");
}

export function hideChatBot() {
  if (!document.getElementById(CHAT_BOT_HIDDEN_STYLE_ID)) {
    const style = document.createElement("style");
    style.id = CHAT_BOT_HIDDEN_STYLE_ID;
    style.innerHTML = `body.chatbot-hidden #chat-widget-container { display: none !important; }`;
    document.head.appendChild(style);
  }
  document.body.classList.add("chatbot-hidden");
}

export function showChatBot() {
  document.body.classList.remove("chatbot-hidden");
}

export function trackSignUp(method: string) {
  window.gtag?.("event", "sign_up", { method });
}

const generateId = (field: string) => field.replaceAll(/[^a-zA-Z0-9]/g, "_");

/**
 * One entry of the distinct-file-properties list. Callers pass either the bare dotted field name
 * as a string, or an object carrying it — hence the union.
 */
type DistinctPropertyValue = string | { field?: string; label?: string; [key: string]: unknown };

/**
 * A node of the property tree these builders emit. The extra members come from the caller-supplied
 * `eachColumnMetadata` / `appendTo*Nodes` spreads, so the shape is open.
 */
type PropertyTreeNode = {
  field: string;
  label: string;
  id: string;
  columns?: PropertyTreeNode[];
  isChecked?: boolean;
  parentId?: string | null;
  [key: string]: unknown;
};

/** Caller-supplied members spread onto every emitted node. */
type NodeExtras = object;

/** The dotted field name an entry contributes: its own string form, or the object's `field`. */
const getRawField = (value: DistinctPropertyValue) => (typeof value === "string" ? value : value.field);

/**
 * The dotted path the tree is split on. `overrideField` callers key the hierarchy off `label`
 * instead of `field`. Undefined when the entry carries neither — it cannot be placed in the tree.
 */
const getName = (value: DistinctPropertyValue, overrideField: boolean) => {
  if (typeof value === "string") return value;
  return overrideField ? value.label : value.field;
};

/** The root node an entry sits under: the name's first dotted segment, or the whole name when it has no dot. */
const getRootSegment = (name: string) => {
  const firstDot = name.indexOf(".");
  return firstDot === -1 ? name : name.slice(0, firstDot);
};

const ensureRootNode = (
  rootNode: string,
  nodeMap: Map<string, PropertyTreeNode>,
  columnData: PropertyTreeNode[],
  appendToNonLeafNodes: NodeExtras,
  eachColumnMetadata: NodeExtras,
) => {
  let rootNodeRef = nodeMap.get(rootNode);

  if (!rootNodeRef) {
    let label = "Metadata";
    if (rootNode === "customProperties") label = "Custom Metadata";
    else if (rootNode === "typeProperties") label = "Type Metadata";

    rootNodeRef = {
      field: rootNode,
      label,
      id: generateId(rootNode),
      columns: [] as PropertyTreeNode[],
      ...appendToNonLeafNodes,
      ...eachColumnMetadata,
    };

    columnData.push(rootNodeRef);
    nodeMap.set(rootNode, rootNodeRef);
  }

  return rootNodeRef;
};

const createNode = (
  fullField: string,
  part: string,
  isLeaf: boolean,
  selectedNodes: string[],
  appendToLeafNodes: NodeExtras,
  appendToNonLeafNodes: NodeExtras,
  eachColumnMetadata: NodeExtras,
): PropertyTreeNode => ({
  field: fullField,
  label: part,
  id: generateId(fullField),
  isChecked: !!selectedNodes.includes(fullField),
  ...(isLeaf ? appendToLeafNodes : appendToNonLeafNodes),
  ...eachColumnMetadata,
});

const buildHierarchy = (
  nameArray: string[],
  rawField: string,
  rootNode: string,
  rootNodeRef: PropertyTreeNode,
  options: {
    nodeMap: Map<string, PropertyTreeNode>;
    selectedNodes: string[];
    appendToLeafNodes: NodeExtras;
    appendToNonLeafNodes: NodeExtras;
    eachColumnMetadata: NodeExtras;
  },
) => {
  const { nodeMap, selectedNodes, appendToLeafNodes, appendToNonLeafNodes, eachColumnMetadata } = options;

  let currentPath = rootNode;
  let parentNode = rootNodeRef;

  for (let i = 0; i < nameArray.length; i++) {
    const part = nameArray[i];
    if (!part) {
      continue;
    }
    const isLeaf = i === nameArray.length - 1;
    const fullField = isLeaf ? rawField : `${currentPath}.${part}`;

    let nodeRef = nodeMap.get(fullField);

    if (!nodeRef) {
      nodeRef = createNode(
        fullField,
        part,
        isLeaf,
        selectedNodes,
        appendToLeafNodes,
        appendToNonLeafNodes,
        eachColumnMetadata,
      );

      if (!isLeaf) nodeRef.columns = [];
      // A leaf's key can also be a group path of a later entry ("a.b", then "a.b/c"), so the node reused as
      // this parent may be a leaf with no `columns`. It gains its child list here, just as the flat builder
      // below gives such a node children through `parentId`.
      (parentNode.columns ??= []).push(nodeRef);
      nodeMap.set(fullField, nodeRef);
    }

    if (!isLeaf) {
      parentNode = nodeRef;
      currentPath = fullField;
    }
  }
};

const processValue = (
  value: DistinctPropertyValue,
  options: {
    nodeMap: Map<string, PropertyTreeNode>;
    columnData: PropertyTreeNode[];
    selectedNodes: string[];
    appendToLeafNodes: NodeExtras;
    appendToNonLeafNodes: NodeExtras;
    eachColumnMetadata: NodeExtras;
    overrideField: boolean;
  },
) => {
  const {
    nodeMap,
    columnData,
    selectedNodes,
    appendToLeafNodes,
    appendToNonLeafNodes,
    eachColumnMetadata,
    overrideField,
  } = options;

  const rawField = getRawField(value);
  const name = getName(value, overrideField);
  // An entry with neither `field` nor `label` has no path to place it under; skip it rather than
  // splitting `undefined` and taking the whole tree down with it.
  if (!rawField || !name) return;

  const rootNode = getRootSegment(name);
  const nameArray = name.split(".").slice(1).join(".").split("/");

  const rootNodeRef = ensureRootNode(rootNode, nodeMap, columnData, appendToNonLeafNodes, eachColumnMetadata);

  const buildHierarchyOptions = {
    nodeMap,
    selectedNodes,
    appendToLeafNodes,
    appendToNonLeafNodes,
    eachColumnMetadata,
  };

  buildHierarchy(nameArray, rawField, rootNode, rootNodeRef, buildHierarchyOptions);
};

export const getTreeViewOfDistinctFileProperties = (
  data: DistinctPropertyValue[],
  eachColumnMetadata: NodeExtras = {},
  selectedNodes: string[] = [],
  appendToLeafNodes: NodeExtras = {},
  appendToNonLeafNodes: NodeExtras = {},
  overrideField = false,
) => {
  const columnData: PropertyTreeNode[] = [];
  const nodeMap = new Map<string, PropertyTreeNode>();

  const processOptions = {
    nodeMap,
    columnData,
    selectedNodes,
    appendToLeafNodes,
    appendToNonLeafNodes,
    eachColumnMetadata,
    overrideField,
  };

  for (const value of data) {
    processValue(value, processOptions);
  }

  return columnData;
};

/** A react-awesome-query-builder field definition, keyed by the property's dotted field name. */
interface QueryBuilderField {
  label?: string | undefined;
  type?: string | undefined;
}

export const generateQueryBuilderConfig = (array: { label: string; fieldType?: string; questionValue?: string }[]) => {
  const config: Record<string, QueryBuilderField> = {};

  array.forEach(({ label, fieldType, questionValue }) => {
    const prefix = label.split(".")[0];
    const finalFieldName = label.split(".").slice(1).join(".").split("/").pop();

    const fieldDefinition: QueryBuilderField = {
      label: finalFieldName,
      type: "text",
    };
    if (prefix === "customProperties") {
      fieldDefinition.type = fieldType;
      fieldDefinition.label = questionValue;
    }
    config[label] = fieldDefinition;
  });
  return config;
};

/**
 * Produces flat tree data with parentId references - O(n) time, O(n) space
 * Use this instead of getTreeViewOfDistinctFileProperties when passing to TreeViewComponent
 * which now handles flat-to-hierarchical conversion internally
 */
export const getFlatTreeViewOfDistinctFileProperties = (
  data: DistinctPropertyValue[],
  eachColumnMetadata: NodeExtras = {},
  selectedNodes: string[] = [],
  appendToLeafNodes: NodeExtras = {},
  appendToNonLeafNodes: NodeExtras = {},
  overrideField = false,
): PropertyTreeNode[] => {
  if (data.length === 0) return [];

  const flatData: PropertyTreeNode[] = [];
  const nodeMap = new Map<string, PropertyTreeNode>();

  for (const value of data) {
    const rawField = getRawField(value);
    const name = getName(value, overrideField);
    // An entry with neither `field` nor `label` has no path to place it under; skip it rather than
    // splitting `undefined` and taking the whole tree down with it.
    if (!rawField || !name) continue;

    const rootNode = getRootSegment(name);
    const nameArray = name.split(".").slice(1).join(".").split("/");

    // Ensure root node exists
    if (!nodeMap.has(rootNode)) {
      let label = "Metadata";
      if (rootNode === "customProperties") label = "Custom Metadata";
      else if (rootNode === "typeProperties") label = "Type Metadata";

      const rootNodeData = {
        field: rootNode,
        label,
        id: rootNode,
        parentId: null,
        ...appendToNonLeafNodes,
        ...eachColumnMetadata,
      };
      nodeMap.set(rootNode, rootNodeData);
      flatData.push(rootNodeData);
    }
    let currentPath = rootNode;

    for (let i = 0; i < nameArray.length; i++) {
      const part = nameArray[i];
      if (!part) continue;

      const isLeaf = i === nameArray.length - 1;
      const fullField = isLeaf ? rawField : `${currentPath}.${part}`;

      if (!nodeMap.has(fullField)) {
        const nodeData = {
          field: fullField,
          label: part,
          id: fullField,
          parentId: currentPath,
          isChecked: !!selectedNodes.includes(fullField),
          ...(isLeaf ? appendToLeafNodes : appendToNonLeafNodes),
          ...eachColumnMetadata,
        };
        nodeMap.set(fullField, nodeData);
        flatData.push(nodeData);
      }

      // Only a non-leaf can parent anything, so only it advances the path.
      if (!isLeaf) currentPath = fullField;
    }
  }

  return flatData;
};
export const fileToBase64 = async (file: Blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (e) => reject(e);
  });

/** An rc-dock layout node: either a box with `children` or a panel with `tabs`. */
interface RcDockNode {
  children?: RcDockNode[];
  tabs?: { id?: string }[];
  activeId?: string;
}

export function removingElementFromRcDock(child: RcDockNode, nonActiveIds: (string | undefined)[]) {
  // A panel carries `tabs` instead of `children`; it, like an empty box, has nothing to recurse into.
  if (child.children && child.children.length > 0) {
    child.children = child.children.filter((nested) => removingElementFromRcDock(nested, nonActiveIds));
  }
  if (child.tabs) {
    if (child.tabs.length > 1) {
      child.tabs = child.tabs.filter((tab) => !nonActiveIds.includes(tab.id));
      if (nonActiveIds.includes(child.activeId)) {
        const firstTabId = child.tabs[0]?.id;
        if (!firstTabId) {
          return false;
        }
        child.activeId = firstTabId;
      }
      return true;
    }
    if (nonActiveIds.includes(child.activeId)) {
      return false;
    }
    return true;
  }
  return true;
}

export const getFileListPageUrl = (searchParams: string) => {
  const url = new URLSearchParams();
  if (searchParams) {
    url.set("search", searchParams);
  }
  return `/Files?byPortal=true&${url.toString()}`;
};

export const getReviewFlowPageUrl = (typeReviewFlow: string) => {
  const url = new URLSearchParams(window.location.search);
  url.set("type", typeReviewFlow);

  const currentPath = window.location.pathname;
  return `${currentPath}?${url.toString()}`;
};

export function isValidJSON(str: string) {
  try {
    JSON.parse(str);
    return true;
  } catch (_error) {
    return false;
  }
}

export const getReviewPageUrl = (typeReview: string, currentSearchParams: string, currentPathname: string) => {
  const urlParams = new URLSearchParams(currentSearchParams);
  urlParams.set("type", typeReview);

  return `${currentPathname}?${urlParams.toString()}`;
};

/**
 * A form-template section as far as the custom-field harvesters here are concerned. `_id` is required on every
 * question by the server's `questionFieldSchema` (forms.schema.ts); it is the key `customFields` is saved under.
 */
interface AnsweredSection {
  questionFields?: { _id: string; answer?: { value?: unknown } }[];
}

// The default `[]` previously typed this parameter `never[]`, which rejected every real caller.
export const getCustomFields = (formTemplate: AnsweredSection[] = []) => {
  const customFields: Record<string, unknown> = {};
  formTemplate.forEach((section) => {
    section.questionFields?.forEach((field) => {
      if (field.answer && field.answer.value) {
        customFields[field._id] = field.answer.value;
      }
    });
  });
  return customFields;
};

/** A folder's file-naming rule set (`folders.fileNomenclature`). */
export interface FileNomenclature {
  separator?: string | undefined;
  fields?:
    | {
        _id?: string | undefined;
        name?: string | undefined;
        type?: string | undefined;
        /** Legacy exact length, superseded by the min/max pair below. */
        allowedCharacters?: number | null | undefined;
        minCharacters?: number | null | undefined;
        maxCharacters?: number | null | undefined;
        options?: string[] | undefined;
      }[]
    | undefined;
}

export const isFileNomenclatureCorrect = (
  name: string | undefined,
  // `null` is how the upload store and the folder documents spell "this folder has no rule"
  // (`FileUploadItem.folder.fileNomenclature`, the upload grid's `fileNomenclature` prop); it answers
  // "compliant", exactly like an absent rule.
  fileNomenclature: FileNomenclature | null = {},
) => {
  // Starts as the raw name and is rewritten into the separator-split segment list below, so the
  // declared type is the union of both forms; each read is asserted to the form it expects.
  let fileName: string | string[] = name || "";
  if (!name || fileNomenclature === null || !fileNomenclature.fields) {
    return true;
  }
  if (name.includes(".")) {
    fileName = name.split(".");
    (fileName as string[]).pop();
  } else {
    fileName = [fileName as string];
  }
  // `separator` is optional on a folder's rule (folders.schema.ts); only template-applied rules always carry one.
  // Without it the name is a single segment, which is also how the server's validator (globalFun.ts) reads it.
  const separator = fileNomenclature.separator;
  const baseName = fileName.join(".");
  fileName = separator === undefined ? [baseName] : baseName.split(separator);

  const fields = fileNomenclature.fields;
  if (!fields.length) {
    return true;
  }

  return fields.every((rule, index: number) => {
    const isLastField = index == fields.length - 1;

    if (isLastField) {
      let lastMax = rule.maxCharacters;
      if (rule.minCharacters == null && lastMax == null && isNonZero(rule.allowedCharacters))
        lastMax = rule.allowedCharacters;
      const lastFreeForm = rule.type == "variable" || (rule.type == "text" && lastMax == null);
      if (!lastFreeForm && fileName.length !== fields.length) return false;
    }

    if (rule.type == "single select") {
      // Ignore blank option slots (a new single select starts with empty
      // placeholders); an all-blank single select imposes no constraint.
      const options = (rule.options ?? []).filter((option: string) => option !== "");
      if (!options.length) return true;
      // A name with fewer segments than rules yields `undefined` here, and `includes(undefined)`
      // returning false IS the intent — so the ARRAY is widened rather than the argument asserted.
      return (options as (string | undefined)[]).includes(fileName[index]);
    }

    // Resolve the character-count range. minCharacters/maxCharacters are the
    // current model; a lone legacy allowedCharacters is an exact length whose
    // pre-min/max semantics (length only, any characters) must be preserved.
    let minChars = rule.minCharacters;
    let maxChars = rule.maxCharacters;
    let legacyExact = false;
    if (minChars == null && maxChars == null && isNonZero(rule.allowedCharacters)) {
      minChars = rule.allowedCharacters;
      maxChars = rule.allowedCharacters;
      legacyExact = true;
    }

    const isUnbounded = maxChars == null;
    // Free-form (any characters, may contain the separator) applies to the
    // legacy "variable" type and to a trailing text field whose max was removed.
    const isFreeForm = rule.type == "variable" || (rule.type == "text" && isUnbounded && isLastField);
    // A trailing free-form field consumes every remaining segment (e.g. "…-FRAMING PLANS").
    const value = isFreeForm && isLastField ? fileName.slice(index).join(separator) : fileName[index];

    if (isFreeForm) {
      if (!value?.trim().length) return false;
      return minChars == null || value.length >= minChars;
    }

    // Legacy exact-length rule → length only, any characters (unchanged behaviour).
    if (legacyExact) {
      return value != null && value.length === rule.allowedCharacters;
    }

    // Fully unconfigured field (no bounds) imposes no constraint.
    if (minChars == null && maxChars == null) {
      return true;
    }

    // Current model: enforce character class (number = digits, text =
    // alphanumeric) plus the configured range, and require a non-empty value.
    const pattern = rule.type == "number" ? /^[0-9]*$/ : /^[A-Za-z0-9]*$/;
    if (value == null || !pattern.test(value)) {
      return false;
    }
    const length = value.length;
    if (length == 0) {
      return false;
    }
    if (minChars != null && length < minChars) {
      return false;
    }
    if (maxChars != null && length > maxChars) {
      return false;
    }
    return true;
  });
};

export const resolveNomenclatureFileName = ({
  originalName,
  nomenclature,
  fileNomenclature,
}: {
  originalName: string;
  nomenclature?: readonly string[] | undefined;
  fileNomenclature?: FileNomenclature | null | undefined;
}): string => {
  const fields = fileNomenclature?.fields ?? [];
  if (!fields.length || !nomenclature?.length) {
    return originalName;
  }

  const values = fields.map((_field, index: number) => nomenclature[index] ?? "");
  if (values.some((value) => value.trim() === "")) {
    return originalName;
  }

  const separator = fileNomenclature?.separator;
  const lastDot = originalName.lastIndexOf(".");
  const extension = lastDot === -1 ? "" : originalName.slice(lastDot + 1);
  const composedName = `${values.join(separator)}${extension ? "." : ""}${extension}`;

  return isFileNomenclatureCorrect(composedName, fileNomenclature) ? composedName : originalName;
};

export type fileUpgradeArgs = {
  resourceType: string;
  sourceFileVersions?: sourceFileArgs[];
  conversionStatus?: string | null;
};

export type sourceFileArgs = {
  conversionStatus?: string | null;
};

export const isFileVersionUpgradePossible = ({
  resourceType,
  sourceFileVersions,
  conversionStatus,
}: fileUpgradeArgs): boolean => {
  let isUpgardePossibe = true;
  if (resourceType == "RESOURCE_TYPE_MERGED_FILE" && sourceFileVersions) {
    sourceFileVersions.forEach((file) => {
      if (file.conversionStatus && !["COMPLETED", "NOT_REQUIRED"].includes(file.conversionStatus)) {
        isUpgardePossibe = false;
      }
    });
  } else if (conversionStatus && !["COMPLETED", "NOT_REQUIRED"].includes(conversionStatus)) {
    isUpgardePossibe = false;
  }

  return isUpgardePossibe;
};

/** Unit symbols extracted from the selected display names, e.g. "Millimetre (mm)" → "mm". */
interface UnitSymbols {
  linearUnit?: string | null;
  areaUnit?: string | null;
  volumeUnit?: string | null;
}

/** `props` with its unit-bearing string values re-expressed in `unitsNew`; every other value is kept as is. */
export function updateUnits<V>(
  props: Record<string, V> | null | undefined = {},
  unitsNew: Record<string, unknown>,
  portalUnits: PortalUnits | null | undefined,
): Record<string, V | string> {
  const lengthsArray = [
    "Length",
    "Width",
    "Height",
    "Thickness",
    "Elevation",
    "Perimeter",
    "Offset",
    "Depth",
    "Distance",
    "Spacing",
  ];

  if (!props || typeof props !== "object") return {};

  // `unitsNew` is the free-form units payload, so its members arrive as `unknown`; anything that is
  // not a display-name string simply has no symbol to extract.
  const extractSymbol = (str: unknown) => {
    if (typeof str !== "string") return null;
    const match = /.*\(([^)]+)\)/.exec(str);
    return match?.[1] || null;
  };

  const unitFactors: Record<string, number> = {};
  [...(portalUnits?.linearUnits ?? []), ...(portalUnits?.areaUnits ?? []), ...(portalUnits?.volumeUnits ?? [])].forEach(
    (portalUnit) => {
      const symbol = extractSymbol(portalUnit.unit);
      if (symbol) unitFactors[symbol] = portalUnit.factor;
    },
  );

  const units: UnitSymbols = {};
  const linearUnit = get(unitsNew, "linearUnit");
  const areaUnit = get(unitsNew, "areaUnit");
  const volumeUnit = get(unitsNew, "volumeUnit");

  if (linearUnit) units.linearUnit = extractSymbol(linearUnit);
  if (areaUnit) units.areaUnit = extractSymbol(areaUnit);
  if (volumeUnit) units.volumeUnit = extractSymbol(volumeUnit);
  const toAreaFactor = units.areaUnit ? unitFactors[units.areaUnit] : undefined;
  const toVolumeFactor = units.volumeUnit ? unitFactors[units.volumeUnit] : undefined;

  return Object.fromEntries(
    Object.entries(props).map(([key, value]): [string, V | string] => {
      if (typeof value !== "string") return [key, value];

      const [numberPart, unit] = value.split(" ");
      // ⚠️ KNOWN BUG: a string whose first word is not a number (a type name such as "Generic 200mm" on a key
      // containing "Width", "Length", "Area" or "Volume") gives NaN here, and the branches below write it out as
      // "NaN <unit>" instead of failing or keeping the value. Another developer will fix.
      const val = Number(numberPart);
      const resolvedUnit = unit ?? "";
      const fromFactor = unitFactors[resolvedUnit];

      if (key.includes("CUBE Area") && isNonZero(fromFactor) && units.areaUnit && isNonZero(toAreaFactor)) {
        const valueInMM = val * fromFactor;
        const newValue = valueInMM / toAreaFactor;
        return [key, `${newValue} ${units.areaUnit}`];
      } else if (
        key.includes("CUBE Volume") &&
        isNonZero(fromFactor) &&
        units.volumeUnit &&
        isNonZero(toVolumeFactor)
      ) {
        const valueInMM = val * fromFactor;
        const newValue = valueInMM / toVolumeFactor;
        return [key, `${newValue} ${units.volumeUnit}`];
      } else if (lengthsArray.some((field) => key.includes(field))) {
        const newUnit = units.linearUnit;
        return newUnit ? [key, `${val} ${newUnit}`] : [key, value];
      } else if (key.includes("Area")) {
        const newUnit = units.areaUnit;
        return newUnit ? [key, `${val} ${newUnit}`] : [key, value];
      } else if (key.includes("Volume")) {
        const newUnit = units.volumeUnit;
        return newUnit ? [key, `${val} ${newUnit}`] : [key, value];
      }

      return [key, value];
    }),
  );
}

export const getIsSocketConnected = (): boolean => {
  // `window.CubeGlobal` is declared `any` on purpose — HCViewerUtils.tsx keeps that registry open
  // because 43 modules hang 79 different members off it — so the one flag read here is narrowed at the
  // read instead. ViewerImpl.tsx / Viewer.tsx only ever assign a boolean to `socketDisconnected`, and a
  // missing registry or missing flag still reads as connected, exactly as the optional chain did.
  const registry: unknown = window.CubeGlobal;
  if (typeof registry !== "object" || registry === null || !("socketDisconnected" in registry)) {
    return true;
  }
  return !registry.socketDisconnected;
};

/**
 * `hwv` off the viewer implementation. A missing impl throws the TypeError the bare `viewerImpl.hwv` read
 * raised (`Cannot read properties of null`): callers reach this only once `hwv` proves the impl exists, so
 * one that breaks that still fails loudly instead of reading as "not loaded".
 */
const readImplViewer = (viewerImpl: unknown): unknown => {
  if (viewerImpl === null || viewerImpl === undefined) {
    throw new TypeError(`getIsViewerDependencyLoaded: viewerImpl is ${String(viewerImpl)} (reading 'hwv')`);
  }
  if (typeof viewerImpl === "object" || typeof viewerImpl === "function") {
    const value: unknown = Reflect.get(viewerImpl, "hwv");
    return value;
  }
  // A primitive has no `hwv`, which the property read answered with `undefined`.
  return undefined;
};

// KNOWN BUG: this reads `viewerImpl.hwv` (`readImplViewer`, which throws on a null impl) even though
// ViewerContext.ts declares `viewerImpl: … | null`.
// Impact: latent - the `&&` chain short-circuits on isModelStructureReady/isSettingsApplied/hwv and no
// provider sets those true while `viewerImpl` is still null. The type half is paid today: tsc cannot
// narrow `viewerImpl` through this signature, which is why SelectionSetItemsTable.tsx casts
// HoopsViewerContext to a NonNullable-viewerImpl shape.
// Fix: make this a type predicate (`): viewerImpl is NonNullable<HoopsViewerImpl>`).
export const getIsViewerDependencyLoaded = ({
  isModelStructureReady,
  isSettingsApplied,
  viewerImpl,
  hwv,
}: {
  isModelStructureReady: boolean;
  isSettingsApplied: boolean;
  // `unknown`: callers pass `HoopsViewerImpl` (whose `hwv` exists only through its `[key: string]: any`
  // index signature) and `window.CubeGlobal.hwv` straight through, and only their truthiness is read here.
  viewerImpl: unknown;
  hwv: unknown;
}): boolean => {
  // Same short-circuit order on the same values as the original `&&` chain; coerced so callers get a
  // real `boolean` rather than the first falsy link.
  return Boolean(
    isModelStructureReady && isSettingsApplied && hwv && readImplViewer(viewerImpl) && getIsSocketConnected(),
  );
};

/**
 * Runs `tasks` with at most `maxConcurrency` in flight at once, preserving input order in the returned
 * results array. Caps the peak working set: only `maxConcurrency` tasks' transient memory is alive at a
 * time, so each task's intermediates become GC-eligible as it settles.
 *
 * Lives here (a leaf util) rather than in `actions/Viewer.ts` so both `Viewer.ts` and
 * `services/utils/modelPropertiesDataDB.ts` can use it without a circular import.
 */
export async function runWithConcurrency(
  tasks: Array<() => Promise<unknown>>,
  maxConcurrency = 5,
  signal?: AbortSignal,
): Promise<unknown[]> {
  const results: Promise<unknown>[] = [];
  const executing: Promise<void>[] = [];

  for (const task of tasks) {
    if (signal?.aborted) break;
    const p = task();
    results.push(p);

    const e: Promise<void> = p.then(() => {
      // `splice` hands back the removed element - here the already-settling `e` itself - so the
      // array of promises it returns is nothing to await.
      void executing.splice(executing.indexOf(e), 1);
    });
    // The caller already gets a failure through Promise.race / Promise.all; don't raise it again as unhandled.
    void e.catch((error: unknown) => error);
    executing.push(e);

    if (executing.length >= maxConcurrency) {
      await Promise.race(executing);
    }
  }

  return Promise.all(results);
}

export const ObjectTreeFieldsMinValuesMapper = {
  fileId: 1,
  fileVersionId: 2,
  cubeUniqueId: 3,
  nodeId: 4,
  parent: 5,
  "CUBE Name": 6,
};
